import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/store';
import {
  Search, Users, LayoutDashboard, Wifi, Megaphone, Zap, CreditCard, Store,
  Coins, Ticket, FileText, Settings, Send, UserCog, Link2, ShieldCheck, Wallet, X,
} from 'lucide-react';

// Telas navegáveis (nome + palavras-chave para achar)
const PAGES = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, keys: 'inicio home painel resumo' },
  { path: '/customers', label: 'Gestor de Clientes', icon: Users, keys: 'clientes gestor listar' },
  { path: '/connections', label: 'Conexões', icon: Wifi, keys: 'whatsapp conexao conectar' },
  { path: '/campaigns', label: 'Campanhas', icon: Megaphone, keys: 'campanha disparo' },
  { path: '/automations', label: 'Automações', icon: Zap, keys: 'automacao fluxo bot' },
  { path: '/gateways', label: 'Gateways de Pagamento', icon: CreditCard, keys: 'gateway pix pagamento asaas mercadopago pagbank' },
  { path: '/integrations', label: 'Minhas Integrações', icon: Link2, keys: 'integracao sigma painelfoda painel' },
  { path: '/resellers', label: 'Revendas', icon: UserCog, keys: 'revenda vendedor sub' },
  { path: '/plans', label: 'Planos', icon: FileText, keys: 'plano pacote' },
  { path: '/credit-shop', label: 'Comprar Créditos', icon: Coins, keys: 'credito comprar recarga' },
  { path: '/coupons', label: 'Cupons', icon: Ticket, keys: 'cupom desconto' },
  { path: '/bulk-send', label: 'Envio em Massa', icon: Send, keys: 'envio massa disparo' },
  { path: '/nexupay', label: 'NexuPay', icon: Wallet, keys: 'carteira nexupay saldo' },
  { path: '/media', label: 'Mídias', icon: FileText, keys: 'midia imagem arquivo' },
  { path: '/store-settings', label: 'Loja', icon: Store, keys: 'loja venda site' },
  { path: '/profile', label: 'Perfil', icon: Settings, keys: 'perfil conta configuracao senha' },
  { path: '/api-integration', label: 'API de Integração', icon: ShieldCheck, keys: 'api chave token webhook' },
];

const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const STATUS_DOT = { active: 'bg-green-500', expiring: 'bg-yellow-500', expired: 'bg-red-500' };

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Ctrl+K / Cmd+K abre; Esc fecha
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    const onOpenEvt = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('open-global-search', onOpenEvt);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('open-global-search', onOpenEvt);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setQuery(''); setCustomers([]); setSel(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // busca de clientes (debounce)
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) { setCustomers([]); return; }
    const t = setTimeout(() => {
      api.get(`/search/global?q=${encodeURIComponent(q)}`)
        .then((r) => setCustomers(r.data.customers || []))
        .catch(() => setCustomers([]));
    }, 250);
    return () => clearTimeout(t);
  }, [query, open]);

  const nq = norm(query);
  const pageMatches = query.trim()
    ? PAGES.filter((p) => norm(p.label).includes(nq) || norm(p.keys).includes(nq)).slice(0, 6)
    : PAGES.slice(0, 6);

  // lista unificada para navegação por teclado
  const items = [
    ...pageMatches.map((p) => ({ type: 'page', ...p })),
    ...customers.map((c) => ({ type: 'customer', ...c })),
  ];

  const go = useCallback((item) => {
    setOpen(false);
    if (item.type === 'page') navigate(item.path);
    else navigate(`/customers?q=${encodeURIComponent(item.name || item.username || item.phone || '')}`);
  }, [navigate]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (items[sel]) go(items[sel]); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4"
         onClick={() => setOpen(false)}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-xl bg-[#0e1524] border border-border rounded-2xl shadow-2xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        {/* input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border/60">
          <Search className="w-5 h-5 text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSel(0); }}
            onKeyDown={onKeyDown}
            placeholder="Buscar cliente ou tela…"
            className="flex-1 bg-transparent outline-none text-base placeholder:text-muted-foreground"
          />
          <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* resultados */}
        <div className="max-h-[60vh] overflow-y-auto py-2">
          {pageMatches.length > 0 && (
            <div className="px-2">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground px-2 py-1">Telas</p>
              {pageMatches.map((p, i) => {
                const idx = i;
                const Icon = p.icon;
                return (
                  <button key={p.path} onClick={() => go({ type: 'page', ...p })}
                    onMouseEnter={() => setSel(idx)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left ${sel === idx ? 'bg-primary/15' : 'hover:bg-muted/40'}`}>
                    <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="text-sm">{p.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          {customers.length > 0 && (
            <div className="px-2 mt-1">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground px-2 py-1">Clientes</p>
              {customers.map((c, i) => {
                const idx = pageMatches.length + i;
                return (
                  <button key={c.id} onClick={() => go({ type: 'customer', ...c })}
                    onMouseEnter={() => setSel(idx)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left ${sel === idx ? 'bg-primary/15' : 'hover:bg-muted/40'}`}>
                    <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[c.status] || 'bg-muted'}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm truncate">{c.name || c.username || '(sem nome)'}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {c.phone || '—'}{c.expiration_date ? ` · vence ${c.expiration_date}` : ''}
                      </p>
                    </div>
                    {c.is_panel && <span className="text-[10px] text-purple-400 shrink-0">Painel</span>}
                  </button>
                );
              })}
            </div>
          )}

          {query.trim().length >= 2 && customers.length === 0 && pageMatches.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">Nada encontrado para "{query}"</p>
          )}
        </div>

        {/* rodapé */}
        <div className="flex items-center gap-4 px-4 py-2 border-t border-border/60 text-[11px] text-muted-foreground">
          <span>↑↓ navegar</span><span>↵ abrir</span><span>esc fechar</span>
          <span className="ml-auto">Ctrl+K</span>
        </div>
      </div>
    </div>
  );
}
